// Where a generated `TODO:` placeholder ends. The server-side DOCX assembler
// (`jenly-azurefunctions-agents`, `common/todo_spans.py`) and the workspace
// frontend apply the same rule, so a TODO is coloured over the same words
// everywhere. Keep the implementations and their test cases in step.

const TODO_PREFIX_RE = /^\s*TODO[:.]\s*/i;
const DOTTED_NUMBER_RE = /^\d+(?:\.\d+)+$/;
const DIGITS_RE = /^\d+$/;
export const TODO_CLOSERS = ")]}\"'”’";
const TODO_MARKER = "TODO[:.]";
const ABBREVIATIONS = new Set(["e.g", "i.e", "vs", "cf", "approx", "fig", "al", "incl"]);

const isWhitespace = (char: string) => /\s/.test(char);

// Letters are told apart by having a case; the ES5 target has no Unicode property escapes.
const isWordChar = (char: string) => /[0-9_]/.test(char) || char.toLowerCase() !== char.toUpperCase();

const isNonTerminalDot = (text: string, dotIndex: number, bodyStart: number): boolean => {
  let tokenStart = dotIndex;
  while (tokenStart > 0 && !isWhitespace(text[tokenStart - 1])) tokenStart -= 1;
  const token = text.slice(tokenStart, dotIndex);
  if (DOTTED_NUMBER_RE.test(token)) return true;
  if (DIGITS_RE.test(token) && tokenStart === bodyStart) return true;
  return ABBREVIATIONS.has(token.toLowerCase());
};

/**
 * Returns the index just after the sentence that ends a TODO, or null when the
 * text holds no complete sentence yet.
 *
 * `text` starts at the TODO marker. A sentence ends at `.`, `!` or `?` (plus any
 * closing quote or bracket) followed by whitespace or the end of the text. A
 * full stop that belongs to a dotted number (`18.2.`), a leading list number
 * (`TODO: 18. Review`), a decimal (`0.21`) or a common abbreviation (`e.g.`)
 * does not end the sentence.
 */
export const findTodoSpanEnd = (text: string): number | null => {
  const prefix = TODO_PREFIX_RE.exec(text);
  const bodyStart = prefix ? prefix[0].length : 0;
  const length = text.length;
  let index = bodyStart;

  while (index < length) {
    const char = text[index];
    if (char !== "." && char !== "!" && char !== "?") {
      index += 1;
      continue;
    }

    let runEnd = index + 1;
    while (runEnd < length && ".!?".includes(text[runEnd])) runEnd += 1;
    let end = runEnd;
    while (end < length && TODO_CLOSERS.includes(text[end])) end += 1;
    if (end < length && !isWhitespace(text[end])) {
      index = runEnd;
      continue;
    }

    if (char === "." && runEnd === index + 1 && isNonTerminalDot(text, index, bodyStart)) {
      index = runEnd;
      continue;
    }
    return end;
  }
  return null;
};

/** Returns the end of the terminating punctuation run (before any closing quote or bracket). */
export const findTodoTerminatorEnd = (text: string): number | null => {
  const end = findTodoSpanEnd(text);
  if (end === null) return null;
  let terminatorEnd = end;
  while (terminatorEnd > 0 && TODO_CLOSERS.includes(text[terminatorEnd - 1])) terminatorEnd -= 1;
  return terminatorEnd;
};

export interface TodoSpan {
  start: number;
  end: number;
}

/**
 * Returns every TODO sentence in `text`, in order. A marker inside a word
 * (`autoTODO:`) is not a placeholder, and a marker inside an earlier TODO
 * sentence belongs to that sentence. A TODO with no complete sentence runs to
 * the end of the text.
 */
export const findTodoSpans = (text: string): TodoSpan[] => {
  const spans: TodoSpan[] = [];
  const marker = new RegExp(TODO_MARKER, "gi");
  let cursor = 0;
  let match = marker.exec(text);
  while (match) {
    const start = match.index;
    if (start >= cursor && !(start > 0 && isWordChar(text[start - 1]))) {
      const end = findTodoSpanEnd(text.slice(start));
      cursor = end === null ? text.length : start + end;
      spans.push({ start, end: cursor });
    }
    match = marker.exec(text);
  }
  return spans;
};

/**
 * Whether the `TODO:` marker at `offset` of `text` is a placeholder: not glued
 * to a preceding word and not inside the sentence of an earlier TODO.
 */
export const isTodoPlaceholderAt = (text: string, offset: number): boolean => {
  if (offset > 0 && isWordChar(text[offset - 1])) return false;
  return !findTodoSpans(text).some((span) => span.start < offset && offset < span.end);
};
